/**
 * @tt/shell-3d — crisp procedural FLOOR texture
 *
 * THE SMEAR FIX. Floors used to bake their flagstone seams / cracks / stone
 * grain into PER-VERTEX COLORS on a coarse low-poly plane. Vertex colors
 * interpolate smoothly between vertices, so any detail finer than the vertex
 * spacing (seams ~0.2u on a ~0.75–1.5u grid) melted into soft blurry blobs —
 * the "muddy smeared purplish surface" the owner saw under the platforms and
 * around the playfield, while small faceted props stayed crisp.
 *
 * This bakes that same detail into a high-resolution tileable CanvasTexture
 * instead. Mapped onto the surface (RepeatWrapping + mipmaps + anisotropy) it
 * reads CRISP at any distance and grazing angle, with no shimmer. Built ONCE
 * (no per-frame cost — important for the owner's CPU budget); callers dispose
 * it on teardown.
 *
 * The geometry keeps its faceted per-vertex height relief (that's real
 * geometry, not the smear) and its toon shading; only the *color detail* moves
 * from vertex colors to this map. Callers may keep a SUBTLE low-frequency
 * vertex tint (rim darken / macro tone) — low-freq interpolates fine and never
 * smears — and let this map carry all the high-frequency crispness.
 */
import * as THREE from 'three';

export interface FloorTextureOptions {
  /** Square canvas resolution in px (power-of-two for mipmaps). Default 1024. */
  resolution?: number;
  /** Base stone color. */
  color: number;
  /** Lit/raised paving color. Default: base lightened toward white. */
  hi?: number;
  /** Sunk mortar / deep-crevice color. Default: base darkened. */
  lo?: number;
  /** Flagstone cells per axis across ONE texture tile. Default 4. */
  cells?: number;
  /** Mortar-seam darkness 0..1 (0 = no seams, organic stone). Default 0.6. */
  mortar?: number;
  /** Per-stone value spread 0..1 (how much stones differ). Default 0.5. */
  variation?: number;
  /** Fine speckle grain amount 0..1. Default 0.22. */
  grain?: number;
  /** Optional warm vein color (e.g. cooled-lava) threaded through the cracks. */
  crackColor?: number;
  /** Crack/vein intensity 0..1 (0 = none). Default 0. */
  cracks?: number;
  /** RepeatWrapping repeats along U (texture-tiles across the surface). Default 1. */
  repeatX?: number;
  /** RepeatWrapping repeats along V. Default 1. */
  repeatY?: number;
  /** Max anisotropy (clamped to the GPU's max at upload). Default 16. */
  anisotropy?: number;
  /** Deterministic RNG. Default Math.random. */
  rng?: () => number;
}

const _w = new THREE.Color(0xffffff);
function cssOf(c: THREE.Color, alpha = 1): string {
  const r = Math.round(c.r * 255);
  const g = Math.round(c.g * 255);
  const b = Math.round(c.b * 255);
  return `rgba(${r},${g},${b},${alpha})`;
}

/**
 * Build a crisp, tileable stone-floor {@link THREE.CanvasTexture}. Assign it to
 * `material.map` and set `material.color = 0xffffff` (toon shading still grades
 * it). The texture is built ONCE; dispose it on teardown.
 */
export function createFloorTexture(opts: FloorTextureOptions): THREE.CanvasTexture {
  const {
    resolution = 1024,
    color,
    cells = 4,
    mortar = 0.6,
    variation = 0.5,
    grain = 0.22,
    cracks = 0,
    repeatX = 1,
    repeatY = 1,
    anisotropy = 16,
    rng = Math.random,
  } = opts;

  const base = new THREE.Color(color);
  const hi = opts.hi !== undefined ? new THREE.Color(opts.hi) : base.clone().lerp(_w, 0.42);
  const lo = opts.lo !== undefined ? new THREE.Color(opts.lo) : base.clone().multiplyScalar(0.5);

  const S = resolution;
  const cv = document.createElement('canvas');
  cv.width = cv.height = S;
  const g = cv.getContext('2d')!;

  // Mortar fills the whole tile first; flagstones are laid on top inset by the
  // seam so the seam reads as a continuous grid (and tiles edge-to-edge: the
  // half-seam at the canvas border meets its mirror on the opposite edge).
  g.fillStyle = cssOf(lo);
  g.fillRect(0, 0, S, S);

  const cell = S / cells;
  // Visible seam fraction scales with `mortar`; 0 → essentially no gap.
  const seamPx = mortar <= 0 ? 0 : Math.max(2, cell * (0.04 + mortar * 0.05));
  // No seams → no rounded-corner grid showing through (organic stone themes).
  const radius = mortar <= 0 ? 0 : cell * 0.1;
  const stone = new THREE.Color();
  const roundRect = (x: number, y: number, w: number, h: number, r: number): void => {
    const rr = Math.min(r, w / 2, h / 2);
    g.beginPath();
    g.moveTo(x + rr, y);
    g.arcTo(x + w, y, x + w, y + h, rr);
    g.arcTo(x + w, y + h, x, y + h, rr);
    g.arcTo(x, y + h, x, y, rr);
    g.arcTo(x, y, x + w, y, rr);
    g.closePath();
  };

  for (let r = 0; r < cells; r++) {
    for (let c = 0; c < cells; c++) {
      const x = c * cell + seamPx / 2;
      const y = r * cell + seamPx / 2;
      const w = cell - seamPx;
      const h = cell - seamPx;
      // per-stone value: lerp base→hi/lo by a signed random spread.
      const v = (rng() - 0.5) * 2; // -1..1
      stone.copy(base).lerp(v > 0 ? hi : lo, Math.abs(v) * variation);
      g.fillStyle = cssOf(stone);
      roundRect(x, y, w, h, radius);
      g.fill();
      // Chiseled relief: top-left bevel highlight + bottom-right shade. Reads
      // as carved blocks catching the key light without faking a light dir the
      // scene contradicts (kept subtle). GATED on mortar: with mortar 0 the
      // floor is organic ground (meadow/dirt/forest), and these per-cell
      // strokes were the phantom "tile grid" that survived tile:0 (owner saw
      // it on banner-bash + splatter-square).
      if (mortar > 0) {
        const bevel = stone.clone().lerp(_w, 0.3);
        g.strokeStyle = cssOf(bevel, 0.45);
        g.lineWidth = Math.max(1.5, cell * 0.02);
        g.beginPath();
        g.moveTo(x, y + h * 0.6);
        g.lineTo(x, y);
        g.lineTo(x + w * 0.6, y);
        g.stroke();
        g.strokeStyle = 'rgba(0,0,0,0.4)';
        g.beginPath();
        g.moveTo(x + w, y + h * 0.4);
        g.lineTo(x + w, y + h);
        g.lineTo(x + w * 0.4, y + h);
        g.stroke();
      }
      // Fine speckle grain inside the stone (stays within bounds → tiles cleanly).
      if (grain > 0) {
        const n = Math.round(cell * cell * 0.004);
        for (let k = 0; k < n; k++) {
          const sx = x + rng() * w;
          const sy = y + rng() * h;
          const d = (rng() - 0.5) * 2 * grain;
          const sc = stone.clone().lerp(d > 0 ? _w : lo, Math.abs(d));
          g.fillStyle = cssOf(sc, 0.5);
          g.fillRect(sx, sy, 2, 2);
        }
      }
    }
  }

  // ── Warm cooled-lava veins threading the cracks (optional) ───────────────
  //   Jagged polylines drawn with additive glow. Each is rendered in a 3×3
  //   neighbor grid (offset by ±S) so any stroke crossing a tile boundary
  //   wraps onto the opposite edge → the texture stays seamlessly tileable.
  if (cracks > 0 && opts.crackColor !== undefined) {
    const crackCol = new THREE.Color(opts.crackColor);
    const veins = Math.max(1, Math.round(cells * 1.5));
    const paths: Array<Array<[number, number]>> = [];
    for (let i = 0; i < veins; i++) {
      const pts: Array<[number, number]> = [];
      let px = rng() * S;
      let py = rng() * S;
      // bias direction roughly along one axis so veins read as fissures, not scribbles
      let ang = rng() * Math.PI * 2;
      const segs = 4 + Math.floor(rng() * 4);
      for (let s = 0; s <= segs; s++) {
        pts.push([px, py]);
        ang += (rng() - 0.5) * 1.1;
        const len = S * (0.05 + rng() * 0.08);
        px += Math.cos(ang) * len;
        py += Math.sin(ang) * len;
      }
      paths.push(pts);
    }
    const prevOp = g.globalCompositeOperation;
    g.globalCompositeOperation = 'lighter';
    g.lineCap = 'round';
    g.lineJoin = 'round';
    for (let oy = -1; oy <= 1; oy++) {
      for (let ox = -1; ox <= 1; ox++) {
        for (const pts of paths) {
          // dim wide halo
          g.strokeStyle = cssOf(crackCol, 0.18 * cracks);
          g.lineWidth = Math.max(3, S * 0.012);
          g.beginPath();
          for (let s = 0; s < pts.length; s++) {
            const [x, y] = pts[s]!;
            if (s === 0) g.moveTo(x + ox * S, y + oy * S);
            else g.lineTo(x + ox * S, y + oy * S);
          }
          g.stroke();
          // hot thin core
          g.strokeStyle = cssOf(crackCol.clone().lerp(_w, 0.35), 0.5 * cracks);
          g.lineWidth = Math.max(1.5, S * 0.004);
          g.stroke();
        }
      }
    }
    g.globalCompositeOperation = prevOp;
  }

  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(repeatX, repeatY);
  tex.anisotropy = anisotropy; // clamped to GPU max at upload
  tex.needsUpdate = true;
  return tex;
}
